import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import useStore from "../../../useStore";
import Loader from "../../Elements/Loader/Loader";
import Button from "../../Elements/Button/Button";
import { useToast } from "../../../Contexts/Toast/toast.provider";
import "./homeRepartidor.css";
import { fmtCurrency } from "../../../Utilities/Moneda/moneda";
import { fmtDate } from "../../../Utilities/Fecha/fecha";
import { buildMapsUrl } from "../../../Utilities/Maps/BuildUri";
import { badgeClassByStatus } from "../../../Utilities/Status/statusColor";

// 👇 NUEVO: geolocalización + utils de distancia
import useGeolocation from "../../../Hooks/useGeolocation";
import {
  annotateWithDistance,
  filterByRadius,
  isValidCoord,
  RADIUS_KM_REPARTIDORES, // = 8 km según tu utils
} from "../../../Utilities/geocode";

export default function HomeRepartidor() {
  const navigate = useNavigate();
  const showToast = useToast();
  const {
    courierOrders,
    loadingCourierOrders,
    errorCourierOrders,
    fetchDataGetOrdersForCourier,
    courierAssignOrder,
    myCourierOrders,
    fetchDataCourierMyOrders,
  } = useStore();

  const [acting, setActing] = useState({});

  // 👇 NUEVO: ubicación del repartidor
  const { status: geoStatus, coords } = useGeolocation();

  useEffect(() => {
    fetchDataGetOrdersForCourier();
  }, [fetchDataGetOrdersForCourier]);

  useEffect(() => {
    if (errorCourierOrders) showToast("error", errorCourierOrders);
  }, [errorCourierOrders, showToast]);

  useEffect(() => {
    fetchDataCourierMyOrders?.();
    const iv = setInterval(() => fetchDataCourierMyOrders?.(), 15000); // opcional
    return () => clearInterval(iv);
  }, [fetchDataCourierMyOrders]);

  const reload = () => fetchDataGetOrdersForCourier();

  const toDeliverCount = useMemo(() => {
    const list = Array.isArray(myCourierOrders) ? myCourierOrders : [];
    return list.filter(o => String(o?.status ?? "").toLowerCase() === "enviado").length;
  }, [myCourierOrders]);

  const wrap = { position: "relative", display: "inline-block" };
  const badgeRing = { position: "absolute", top: -8, right: -8, width: 24, height: 24, borderRadius: "999px", backgroundColor: "rgba(99,102,241,0.18)", zIndex: 10 };
  const badge = { position: "absolute", top: -10, right: -10, minWidth: 20, height: 20, padding: "0 6px", borderRadius: "999px", background: "#ef4444", border: "2px solid #fff", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 20, boxShadow: "0 2px 6px rgba(239,68,68,.6)" };
  const badgeText = { color: "#fff", fontWeight: 900, fontSize: 11 };

  const handleTomarPedido = async (order) => {
    try {
      setActing((p) => ({ ...p, [order.orderId]: true }));
      const res = await courierAssignOrder(order.orderId);

      if (res?.statusCode === 200) {
        showToast("success", `Has tomado el pedido #${order.orderId} 🚚`);
        await fetchDataGetOrdersForCourier();
      } else {
        const msg = res?.message || res?.body?.message || "No se pudo tomar el pedido.";
        showToast("error", msg);
      }
    } catch (e) {
      showToast("error", e?.message || "Error al tomar el pedido.");
    } finally {
      setActing((p) => ({ ...p, [order.orderId]: false }));
    }
  };

  // 1) Base: pedidos disponibles (no asignados y en estado preparándose)
  const disponiblesBase = useMemo(() => {
    return (courierOrders || []).filter((o) => {
      const assigned = (o.repartidorAsignado ?? "").toString().trim();
      return (!assigned || assigned === "") && o.status === "preparando";
    });
  }, [courierOrders]);

  // 2) Filtrado por radio de 8 km usando pickup.lat/lng (o fallback si tu API usa otras llaves)
  const disponiblesEnRadio = useMemo(() => {
    if (geoStatus !== "granted" || !coords) return [];



    // Normalizamos lat/lng desde pickup (ajusta si tus llaves son otras)
    const conCoords = disponiblesBase
      .map((o) => {
        const lat = o?.pickup?.lat ?? o?.lat;
        const lng = o?.pickup?.lng ?? o?.lng;
        return { ...o, lat, lng };
      })
      .filter((o) => isValidCoord(o.lat, o.lng));

    const withDist = annotateWithDistance(coords, conCoords);
    return filterByRadius(withDist, RADIUS_KM_REPARTIDORES);
  }, [disponiblesBase, geoStatus, coords]);

  // 3) Render según estado de geolocalización
  if (loadingCourierOrders) return <Loader />;

  if (geoStatus === "pending") {
    return (
      <div className="container-fluid container-mh py-3 home-repartidor">
        <div className="alert alert-info mb-0">Solicitando tu ubicación…</div>
      </div>
    );
  }

  if (geoStatus !== "granted") {
    return (
      <div className="container-fluid container-mh py-3 home-repartidor">
        <div className="row align-items-center g-2 mb-3">
          <div className="col-12 col-lg">
            <h2 className="mb-0">🚚 Pedidos disponibles</h2>
          </div>
          <div className="col-12 col-lg-auto">
            <div className="d-grid gap-2 d-sm-flex">
              <Button text="Refrescar" color="blue" fill action={reload} />
              <div style={wrap}>
                <Button
                  text="Pedidos por entregar"
                  color="green"
                  fill
                  action={() => navigate("/courier/my-orders")}
                />
                {toDeliverCount > 0 && (
                  <>
                    <span style={badgeRing} />
                    <span style={badge}>
                      <span style={badgeText}>{toDeliverCount > 99 ? "99+" : toDeliverCount}</span>
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="alert alert-warning mb-0">
          Para ver pedidos cercanos necesitas habilitar tu ubicación (≤ {RADIUS_KM_REPARTIDORES} km).
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid container-mh py-3 home-repartidor">
      {/* Header */}
      <div className="row align-items-center g-2 mb-3">
        <div className="col-12 col-lg">
          <h2 className="mb-0">🚚 Pedidos disponibles</h2>
          <small className="text-muted">Mostrando pedidos a ≤ {RADIUS_KM_REPARTIDORES} km de ti</small>
        </div>
        <div className="col-12 col-lg-auto">
          <div className="d-grid gap-2 d-sm-flex">
            <Button text="Refrescar" color="blue" fill action={reload} />
            <div style={wrap}>
              <Button
                text="Pedidos por entregar"
                color="green"
                fill
                action={() => navigate("/courier/my-orders")}
              />
              {toDeliverCount > 0 && (
                <>
                  <span style={badgeRing} />
                  <span style={badge}>
                    <span style={badgeText}>{toDeliverCount > 99 ? "99+" : toDeliverCount}</span>
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {errorCourierOrders && (
        <div className="row">
          <div className="col-12">
            <div className="alert alert-danger">{errorCourierOrders}</div>
          </div>
        </div>
      )}

      {disponiblesEnRadio.length === 0 ? (
        <div className="row">
          <div className="col-12">
            <div className="alert alert-warning mb-0">
              No hay pedidos dentro de {RADIUS_KM_REPARTIDORES} km.
            </div>
          </div>
        </div>
      ) : (
        <div className="row g-3">
          {disponiblesEnRadio.map((o) => {
            const isBusy = !!acting[o.orderId];
            const mapsUrl = buildMapsUrl(o.pickup);
            const tip = Number(o.tip || 0);
            const pickup = o?.pickup || {};
            const distanceText =
              typeof o._distanceKm === "number"
                ? `${o._distanceKm.toFixed(2)} km`
                : null;

            return (
              <div className="col-12 col-md-6 col-lg-4" key={o.orderId}>
                <div className="card pr-card h-100 shadow-sm">
                  <div className="card-body d-flex flex-column">
                    <div className="d-flex justify-content-between align-items-start">
                      <h5 className="card-title mb-1">#{o.orderId}</h5>
                      <span className={`badge bg-${badgeClassByStatus(o.status)}`}>
                        {o.status}
                      </span>
                    </div>

                    <p className="text-muted mb-2">{fmtDate(o.createdAt)}</p>
                    <p className="mb-0">
                      <strong>{o.restauranteNombre || "Restaurante"}</strong>
                      {distanceText && (
                        <span className="ms-2 badge bg-secondary">{distanceText}</span>
                      )}
                    </p>

                    {pickup?.direccion && (
                      <div className="mt-2">
                        <small className="text-muted d-block">Recoger en:</small>
                        <div className="delivery-address" title={pickup.direccion}>
                          {pickup.direccion}
                        </div>
                      </div>
                    )}

                    <hr className="my-2" />

                    <div className="d-flex justify-content-between">
                      <span>Subtotal</span>
                      <span>{fmtCurrency(o.subtotal)}</span>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span>Envío</span>
                      <span>{fmtCurrency(o.shippingCost)}</span>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span>Propina</span>
                      <span>{fmtCurrency(tip)}</span>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span><strong>Total</strong></span>
                      <strong className="text-primary">{fmtCurrency(o.totalAmount)}</strong>
                    </div>

                    <div className="mt-auto d-grid gap-2 pt-3 d-sm-flex">
                      <Button
                        text="Ver en Google Maps"
                        color="blue"
                        fill
                        action={() => window.open(mapsUrl, "_blank", "noopener,noreferrer")}
                      />

                      <Button
                        text={isBusy ? "Procesando..." : "Tomar Pedido"}
                        color="green"
                        fill
                        disabled={isBusy /* ya validamos estado y radio arriba */}
                        action={() => handleTomarPedido(o)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}